/** Wake lock, voice guidance, install detection — small platform wrappers. */

let wakeLock: WakeLockSentinel | null = null
let wantLock = false

async function acquire(): Promise<void> {
  if (!('wakeLock' in navigator)) return
  try {
    wakeLock = await navigator.wakeLock.request('screen')
    wakeLock.addEventListener('release', () => {
      wakeLock = null
    })
  } catch {
    // denied (low battery etc.) — recording continues, screen may sleep
  }
}

/** Keep the screen on (auto re-acquires when the tab becomes visible again). */
export function keepScreenOn(on: boolean): void {
  wantLock = on
  if (on) void acquire()
  else {
    void wakeLock?.release()
    wakeLock = null
  }
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && wantLock && !wakeLock) void acquire()
})

let voiceEnabled = false

export function setVoiceEnabled(on: boolean): void {
  voiceEnabled = on
  if (!on) window.speechSynthesis?.cancel()
}

export function speak(text: string): void {
  if (!voiceEnabled || !('speechSynthesis' in window)) return
  window.speechSynthesis.cancel()
  const u = new SpeechSynthesisUtterance(text)
  u.lang = 'en-US'
  u.rate = 1.05
  window.speechSynthesis.speak(u)
}

/** Human-readable, iPhone-aware guidance for GeolocationPositionError codes. */
export function friendlyGeoError(code?: number): string {
  if (code === 1) {
    return isIos()
      ? 'Location access is blocked. On iPhone: Settings → Privacy & Security → Location Services → Safari Websites → “While Using the App”. In Safari, also tap the “aA”/page menu → Website Settings → Location → Allow, then reload.'
      : 'Location access is blocked — allow it for this site in your browser settings, then reload.'
  }
  if (code === 2) return 'No GPS fix right now — make sure you are outdoors with a clear view of the sky.'
  if (code === 3) return 'GPS is taking long — still trying…'
  return 'Location is unavailable on this device.'
}

/** Best-effort check whether geolocation permission is already denied. */
export async function geoPermissionDenied(): Promise<boolean> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' })
    return status?.state === 'denied'
  } catch {
    return false
  }
}

export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)

export const isIos = (): boolean => /iphone|ipad|ipod/i.test(navigator.userAgent)
